import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from 'react';
import { useToast } from '../contexts/ToastContext';
import { RosterMember, UserRole, ArtistProfile } from '../types';
import { API_BASE_URL } from '@/config';
import { useData } from './DataContext';
import { loggingService } from '@/services/loggingService';
import { LogLevel, LogCategory } from '@/types/logs';

// Helper for safe storage
const createSafeStorage = () => {
  try {
    window.localStorage.setItem('__test', '1');
    window.localStorage.removeItem('__test');
    return window.localStorage;
  } catch {
    console.warn('localStorage is not available. Using in-memory storage.');
    const inMemoryStore: { [key: string]: string } = {};
    return {
      getItem: (key: string) => inMemoryStore[key] || null,
      setItem: (key: string, value: string) => { inMemoryStore[key] = value; },
      removeItem: (key: string) => { delete inMemoryStore[key]; },
      clear: () => { Object.keys(inMemoryStore).forEach(key => delete inMemoryStore[key]); }
    };
  }
};

const safeStorage = createSafeStorage();

interface AuthContextType {
  currentUser: RosterMember | null;
  token: string | null;
  setToken: (token: string | null) => void;
  loading: boolean;
  walletConnecting: boolean;
  isNewWeb3User: boolean;
  setIsNewWeb3User: (isNew: boolean) => void;
  login: (
    role: UserRole,
    method: 'web2' | 'web3',
    credentials?: { email: string; password: string }
  ) => Promise<void>;
  logout: () => void;
  signup: (profile: ArtistProfile) => Promise<void>;
  setCurrentUser: (user: RosterMember | null) => void;
  connectWallet: (walletAddress: string) => Promise<RosterMember | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<RosterMember | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [walletConnecting, setWalletConnecting] = useState(false);
  const [isNewWeb3User, setIsNewWeb3User] = useState(false);
  const { notify } = useToast();
  const { findUserByEmail, addUser } = useData();

  const handleSetToken = useCallback((newToken: string | null) => {
    setToken(newToken);
    if (newToken) {
      safeStorage.setItem('kk_token', newToken);
    } else {
      safeStorage.removeItem('kk_token');
    }
  }, []);

  // This hook runs ONCE on app load to restore the user session.
  useEffect(() => {
    setLoading(true);
    try {
      const storedToken = safeStorage.getItem('kk_token');
      const storedUser = safeStorage.getItem('kk_currentUser');

      if (storedUser && storedToken) {
        const user: RosterMember = JSON.parse(storedUser);
        setCurrentUser(user);
        // Use handleSetToken to ensure state and localStorage are in sync.
        // This is the single source of truth for setting the token.
        handleSetToken(storedToken);
        loggingService.log('Restored user session from storage', LogLevel.INFO, LogCategory.AUTH, { userId: user.id });
      } else {
          // If there's any issue, ensure a clean slate.
          logout(false); // Call logout without reload, just to clean up.
      }
    } catch (error) {
      loggingService.log('Could not restore session, clearing storage.', LogLevel.WARN, LogCategory.AUTH, { error });
      logout(false); // Call logout without reload on error as well.
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectWallet = useCallback(async (walletAddress: string): Promise<RosterMember | null> => {
    if (!walletAddress) return null;

    loggingService.log('connectWallet called', LogLevel.INFO, LogCategory.AUTH, { walletAddress });
    setWalletConnecting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ walletAddress }),
        credentials: 'include',
      });

      const data = await response.json();
      loggingService.log('Login API response', LogLevel.INFO, LogCategory.AUTH, { status: response.status, data });

      if (response.ok) {
        const { user, token: apiToken } = data;
        setCurrentUser(user);
        handleSetToken(apiToken);
        safeStorage.setItem('kk_currentUser', JSON.stringify(user));
        notify(`Welcome back, ${user.name}!`, 'success');
        setIsNewWeb3User(false);
        return user;
      } else if (response.status === 404) {
        setCurrentUser(null);
        handleSetToken(null);
        safeStorage.removeItem('kk_currentUser');
        notify(data.message || 'Please complete your registration.', 'info');
        setIsNewWeb3User(true);
      } else {
        throw new Error(data.message || 'An error occurred during login.');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'A network error occurred.';
      notify(errorMessage, 'error');
      setCurrentUser(null);
      handleSetToken(null);
      safeStorage.removeItem('kk_currentUser');
    } finally {
      setWalletConnecting(false);
    }
    return null;
  }, [notify, handleSetToken]);

  const login = useCallback(
    async (role: UserRole, method: 'web2' | 'web3', credentials?: { email: string; password: string }) => {
      if (method === 'web3') {
        return;
      }

      if (!credentials) {
        notify('Credentials required for this login method.', 'error');
        return;
      }
      
      setLoading(true);
      try {
        const { email, password } = credentials;
        
        const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        });

        const data = await response.json();

        if (!response.ok) {
            throw new Error(data.error || 'An unknown login error occurred.');
        }
        
        const { user, token: apiToken } = data;

        if (user.role?.toUpperCase() !== role.toUpperCase()) {
            notify(`Role Mismatch: Your account is a ${user.role}, not a ${role}.`, 'error');
            return;
        }

        setCurrentUser(user);
        handleSetToken(apiToken);
        safeStorage.setItem('kk_currentUser', JSON.stringify(user));

        notify(`Welcome back, ${user.name}!`, 'success');
        loggingService.log('User logged in via web2', LogLevel.INFO, LogCategory.AUTH, { email: user.email });

        window.location.href = '/dashboard';

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'A network error occurred.';
        notify(errorMessage, 'error');
        setCurrentUser(null);
        handleSetToken(null);
        safeStorage.removeItem('kk_currentUser');
      } finally {
        setLoading(false);
      }
    },
    [notify, handleSetToken]
  );

  const signup = useCallback(
    async (profile: ArtistProfile) => {
      if (findUserByEmail(profile.email)) {
        notify('Registration Failed: An account with this email already exists.', 'error');
        return;
      }
      await addUser(profile);
      const newUser = findUserByEmail(profile.email);
      if (newUser) {
        setCurrentUser(newUser);
        handleSetToken(null); // A new signup does not grant a token
        safeStorage.setItem('kk_currentUser', JSON.stringify(newUser));
        notify(`Account created for ${newUser.name}! Please complete your profile.`, 'success');
      } else {
        notify('Error creating account.', 'error');
      }
    },
    [findUserByEmail, addUser, notify, handleSetToken]
  );

  const logout = useCallback(async (shouldReload = true) => {
    const userId = currentUser?.id;
    loggingService.log('Initiating user logout', LogLevel.INFO, LogCategory.AUTH, { userId, shouldReload });

    // 1. Clear React State immediately
    setCurrentUser(null);
    handleSetToken(null);
    
    // 2. Clear all localStorage and sessionStorage
    safeStorage.clear();
    window.sessionStorage.clear(); // Also clear session storage for good measure
    
    // 3. Unregister all service workers to destroy any caching.
    // This is the definitive fix for the logout bug.
    if ('serviceWorker' in navigator) {
        try {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for (const registration of registrations) {
                await registration.unregister();
                loggingService.log('Service Worker unregistered successfully', LogLevel.INFO, LogCategory.AUTH);
            }
        } catch (error) {
            loggingService.log('Service Worker unregistration failed.', LogLevel.ERROR, LogCategory.AUTH, { error });
            console.error("Service Worker unregistration failed: ", error);
        }
    }

    // 4. If this logout call should trigger a UI refresh...
    if (shouldReload) {
        notify('You have been securely logged out.', 'success');
        // 5. Force a hard reload from the server, bypassing any remaining cache.
        window.location.href = '/';
    }
  }, [notify, currentUser, handleSetToken]);

  const value = { 
    currentUser, 
    token,
    setToken: handleSetToken,
    loading,
    walletConnecting,
    login, 
    logout, 
    signup, 
    setCurrentUser, 
    connectWallet, 
    isNewWeb3User, 
    setIsNewWeb3User 
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
